/**
 * StartDraftBanner — iter87.6. Prominent call-out on the tournament
 * dashboard when the admin still needs to kick off a draft. Same
 * visual family as EmailRecoveryBanner — dismissable, per-tournament
 * flag so operator can hide it and come back.
 *
 * iter87.174 — Dismissal moved from `localStorage` → `sessionStorage`
 * so the banner returns on every re-entry (fresh login, tab reopen,
 * Pro+ / Tester surface switch). The persistent home for the
 * "Start Auto-Draft" action is the new AdminPanel Draft tile; the
 * banner is now purely a session-scoped nudge.
 *
 * Only renders when:
 *   - a tournament is selected AND
 *   - the current user is the rivalry (or co-)admin AND
 *   - `isDraftEligible(tournament)` from the shared whitelist AND
 *   - no draft exists yet for THIS tournament AND
 *   - no orphan draft-complete assignments already recovered
 *
 * iter87.465.22 (user-directed) — rebuilt from a per-template
 * blacklist into the shared `isDraftEligible` helper (see that file's
 * header) so this banner, AdminPanel's Draft tab, and HeroStrip's
 * Start/Join Draft buttons can never drift apart again.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, X } from "lucide-react";
import { isDraftEligible } from "@/lib/draftEligibleTemplates";

const KEY = (leagueId, tid) => `tcr_start_draft_banner_${leagueId}_${tid || "none"}_dismissed`;

export default function StartDraftBanner({ leagueId, tournament, me, draft, hasOrphanAssignments }) {
    const tid = tournament?.id;
    const [dismissed, setDismissed] = useState(() => {
        try { return !!sessionStorage.getItem(KEY(leagueId, tid)); } catch { return false; }
    });

    if (!tournament || !tid) return null;
    if (!(me?.is_admin || me?.is_co_admin)) return null;
    if (draft) return null;
    if (hasOrphanAssignments) return null;
    // iter87.35 — Never show the "Kick off the draft" CTA for a
    // tournament that has already concluded. Position Draft
    // tournaments in `lifecycle_state == "final"` were still showing
    // the banner (and routing to a "no draft to run" backend error).
    if (tournament?.lifecycle_state === "final") return null;
    if (tournament?.completion_authorized_by) return null;
    // iter87.465.22 — Shared whitelist helper: only show for a
    // genuine admin-run draft template, never in pot pick'em mode.
    if (!isDraftEligible(tournament)) return null;
    if (dismissed) return null;

    const dismiss = () => {
        try { sessionStorage.setItem(KEY(leagueId, tid), "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className="brutal-card-light p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-volt/40"
            data-testid="start-draft-banner"
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm">Kick off the draft</p>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        The tournament is set up — nothing scores until crewmates get their teams. Start the draft now, or dismiss and come back.
                    </p>
                </div>
            </div>
            <div className="flex gap-2 shrink-0 self-center sm:self-auto">
                <Link
                    to={`/league/${leagueId}/draft`}
                    data-testid="start-draft-banner-btn"
                    className="brutal-btn text-xs py-2 bg-volt text-black border-black shadow-brutal"
                >
                    <Zap className="w-3.5 h-3.5" /> Start Draft
                </Link>
                <button
                    onClick={dismiss}
                    className="brutal-btn-ghost p-2"
                    aria-label="Dismiss"
                    data-testid="dismiss-start-draft-banner-btn"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
